import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { SystemPerformanceTracker, SystemBetResult } from '../../types/system';
import LoadingOverlay from '../LoadingOverlay';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface DailyPL {
  date: string;
  cumulativePL: number;
}

export default function SystemPerformance() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [performanceData, setPerformanceData] = useState<SystemPerformanceTracker[]>([]);
  const [showRecordBetForm, setShowRecordBetForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [betToDelete, setBetToDelete] = useState<{ id: string; date: string; runner: string } | null>(null);
  const [chartData, setChartData] = useState<DailyPL[]>([]);
  const [isTableExpanded, setIsTableExpanded] = useState(true);
  const [isChartExpanded, setIsChartExpanded] = useState(true);
  const [newBet, setNewBet] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().split(' ')[0].slice(0, 5),
    runner: '',
    stake: '',
    odds: '',
    result: 'pending' as SystemBetResult,
  });

  useEffect(() => {
    if (user) {
      fetchPerformanceData();
    }
  }, [user]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('system_performance_tracker')
        .select('*')
        .eq('user_id', user!.id)
        .order('date', { ascending: false })
        .limit(100);

      if (fetchError) throw fetchError;

      if (data) {
        setPerformanceData(data);
        processChartData(data);
      }
    } catch (err) {
      console.error('Error fetching performance data:', err);
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (data: SystemPerformanceTracker[]) => {
    // Sort data by date ascending
    const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    // Group bets by date and calculate daily cumulative P/L
    const dailyData = sortedData.reduce((acc: { [key: string]: number }, bet) => {
      const date = bet.date.split('T')[0];
      acc[date] = (acc[date] || 0) + bet.pl;
      return acc;
    }, {});

    // Convert to array and calculate cumulative P/L
    let runningTotal = 0;
    const chartData = Object.entries(dailyData).map(([date, pl]) => {
      runningTotal += pl;
      return {
        date: formatChartDate(date),
        cumulativePL: Number(runningTotal.toFixed(2))
      };
    });

    setChartData(chartData);
  };

  const formatChartDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' }).toUpperCase();
    return `${day}-${month}`;
  };

  const calculatePL = (stake: number, odds: number, result: SystemBetResult): number => {
    switch (result) {
      case 'won':
        return Number((stake * (odds - 1)).toFixed(2));
      case 'lost':
        return Number((-stake).toFixed(2));
      case 'pending':
      case 'void':
      default:
        return 0;
    }
  };

  const updateDailyPerformance = async (date: string) => {
    try {
      // Convert the date to UTC midnight for start and end of day
      const targetDate = new Date(date.split('T')[0]);
      const startOfDay = new Date(targetDate);
      startOfDay.setUTCHours(0, 0, 0, 0);
      const endOfDay = new Date(targetDate);
      endOfDay.setUTCHours(23, 59, 59, 999);

      // Get all bets for the day using proper date range
      const { data: dayBets, error: fetchError } = await supabase
        .from('system_performance_tracker')
        .select('*')
        .eq('user_id', user!.id)
        .gte('date', startOfDay.toISOString())
        .lte('date', endOfDay.toISOString());

      if (fetchError) throw fetchError;

      if (dayBets) {
        // Filter out void bets for total P/L calculation
        const validBets = dayBets.filter(bet => bet.result !== 'void');
        const bets_placed = validBets.length;
        const total_pl = validBets.reduce((sum, bet) => sum + (bet.pl || 0), 0);
        const status = total_pl > 0 ? 'Achieved' : bets_placed >= 8 ? 'Not Achieved' : 'In Progress';

        console.log('Updating daily performance:', {
          date: date.split('T')[0],
          bets_placed,
          total_pl,
          status
        });

        // Update or insert daily performance record with proper conflict handling
        const { error: upsertError } = await supabase
          .from('system_daily_performance')
          .upsert(
            {
              user_id: user!.id,
              date: date.split('T')[0],
              bets_placed,
              total_pl,
              status,
              updated_at: new Date().toISOString()
            },
            {
              onConflict: 'user_id,date',
              ignoreDuplicates: false
            }
          );

        if (upsertError) throw upsertError;
      }
    } catch (err) {
      console.error('Error updating daily performance:', err);
    }
  };

  const handleRecordBet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      const stake = parseFloat(newBet.stake);
      const odds = parseFloat(newBet.odds);
      
      if (isNaN(stake) || isNaN(odds)) {
        throw new Error('Invalid stake or odds value');
      }

      const pl = calculatePL(stake, odds, newBet.result);
      const dateTime = `${newBet.date}T${newBet.time}:00Z`;

      const { data, error: insertError } = await supabase
        .from('system_performance_tracker')
        .insert([
          {
            user_id: user.id,
            date: dateTime,
            runner: newBet.runner,
            stake: stake,
            odds: odds,
            result: newBet.result,
            pl: pl,
          }
        ])
        .select();

      if (insertError) throw insertError;

      // Update daily performance
      await updateDailyPerformance(dateTime);

      // Reset form and refresh data
      setNewBet({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0].slice(0, 5),
        runner: '',
        stake: '',
        odds: '',
        result: 'pending',
      });
      setShowRecordBetForm(false);
      await fetchPerformanceData();
    } catch (err) {
      console.error('Error recording bet:', err);
      setError('Failed to record bet');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateResult = async (betId: string, newResult: SystemBetResult) => {
    try {
      setError(null);
      const bet = performanceData.find(b => b.id === betId);
      if (!bet) return;

      const pl = calculatePL(bet.stake, bet.odds, newResult);

      const { error: updateError } = await supabase
        .from('system_performance_tracker')
        .update({ result: newResult, pl })
        .eq('id', betId);

      if (updateError) throw updateError;

      // Update daily performance after updating bet result
      await updateDailyPerformance(bet.date);

      await fetchPerformanceData();
    } catch (err) {
      console.error('Error updating bet result:', err);
      setError('Failed to update bet result');
    }
  };

  const handleDeleteBet = async (betId: string, date: string) => {
    try {
      setError(null);
      
      const { error: deleteError } = await supabase
        .from('system_performance_tracker')
        .delete()
        .eq('id', betId);

      if (deleteError) throw deleteError;

      // Update daily performance after deleting bet
      await updateDailyPerformance(date);
      await fetchPerformanceData();
    } catch (err) {
      console.error('Error deleting bet:', err);
      setError(typeof err === 'string' ? err : 'Failed to delete bet');
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' }).toUpperCase();
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const calculateCumulativePL = (bets: SystemPerformanceTracker[], currentIndex: number): number => {
    return bets
      .slice(currentIndex)
      .reduce((sum, bet) => sum + bet.pl, 0);
  };

  const handleDeleteConfirm = async () => {
    if (!betToDelete) return;
    
    await handleDeleteBet(betToDelete.id, betToDelete.date);
    setShowDeleteConfirmation(false);
    setBetToDelete(null);
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="space-y-6">
      {/* Record Bet Banner */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-primary-50">
          <div className="flex items-center justify-between flex-wrap sm:flex-nowrap">
            <div>
              <h3 className="text-lg font-medium text-primary-900">Track Your Performance</h3>
              <p className="mt-1 text-sm text-primary-600">
                Keep track of your betting performance by recording each bet. This helps you monitor your progress and make data-driven decisions.
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setShowRecordBetForm(!showRecordBetForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {showRecordBetForm ? (
                  <>
                    <svg className="mr-1.5 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel
                  </>
                ) : (
                  <>
                    <svg className="mr-1.5 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Record Bet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Record Bet Form */}
      {showRecordBetForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">Record New Bet</h3>
          <form onSubmit={handleRecordBet} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  id="date"
                  value={newBet.date}
                  onChange={(e) => setNewBet(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm h-10"
                  required
                />
              </div>

              <div>
                <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                  Time
                </label>
                <input
                  type="time"
                  name="time"
                  id="time"
                  value={newBet.time}
                  onChange={(e) => setNewBet(prev => ({ ...prev, time: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm h-10"
                  required
                />
              </div>

              <div>
                <label htmlFor="runner" className="block text-sm font-medium text-gray-700">
                  Runner
                </label>
                <input
                  type="text"
                  name="runner"
                  id="runner"
                  value={newBet.runner}
                  onChange={(e) => setNewBet(prev => ({ ...prev, runner: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm h-10"
                  required
                />
              </div>

              <div>
                <label htmlFor="stake" className="block text-sm font-medium text-gray-700">
                  Stake (£)
                </label>
                <input
                  type="number"
                  name="stake"
                  id="stake"
                  step="0.01"
                  min="0"
                  value={newBet.stake}
                  onChange={(e) => setNewBet(prev => ({ ...prev, stake: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm h-10"
                  required
                />
              </div>

              <div>
                <label htmlFor="odds" className="block text-sm font-medium text-gray-700">
                  Odds
                </label>
                <input
                  type="number"
                  name="odds"
                  id="odds"
                  step="0.01"
                  min="1.01"
                  value={newBet.odds}
                  onChange={(e) => setNewBet(prev => ({ ...prev, odds: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm h-10"
                  required
                />
              </div>

              <div>
                <label htmlFor="result" className="block text-sm font-medium text-gray-700">
                  Result
                </label>
                <select
                  name="result"
                  id="result"
                  value={newBet.result}
                  onChange={(e) => setNewBet(prev => ({ ...prev, result: e.target.value as SystemBetResult }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm h-10"
                  required
                >
                  <option value="pending">Pending</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="void">Void</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowRecordBetForm(false)}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {saving ? 'Recording...' : 'Record Bet'}
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Performance Chart */}
      {performanceData.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <button
              onClick={() => setIsChartExpanded(!isChartExpanded)}
              className="w-full flex justify-between items-center focus:outline-none"
            >
              <h2 className="text-lg font-medium text-gray-900">Cumulative P/L Chart</h2>
              <svg
                className={`h-5 w-5 text-gray-500 transform transition-transform ${
                  isChartExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {isChartExpanded && (
            <div className="p-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value: number) => [`£${value.toFixed(2)}`, 'Cumulative P/L']}
                    />
                    <Line
                      type="monotone"
                      dataKey="cumulativePL"
                      stroke="#8884d8"
                      dot={false}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <button
            onClick={() => setIsTableExpanded(!isTableExpanded)}
            className="w-full flex justify-between items-center focus:outline-none"
          >
            <h2 className="text-lg font-medium text-gray-900">Betting History</h2>
            <svg
              className={`h-5 w-5 text-gray-500 transform transition-transform ${
                isTableExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {isTableExpanded && (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date/Time
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Runner
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stake
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Odds
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Result
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      P/L
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cumulative P/L
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {performanceData.map((bet, index) => (
                    <tr key={bet.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(bet.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {bet.runner}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        £{bet.stake.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {bet.odds.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        {bet.result === 'pending' ? (
                          <select
                            value={bet.result}
                            onChange={(e) => handleUpdateResult(bet.id, e.target.value as SystemBetResult)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm h-10"
                          >
                            <option value="pending">Pending</option>
                            <option value="won">Won</option>
                            <option value="lost">Lost</option>
                            <option value="void">Void</option>
                          </select>
                        ) : (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            bet.result === 'won' ? 'bg-green-100 text-green-800' :
                            bet.result === 'lost' ? 'bg-red-100 text-red-800' :
                            bet.result === 'void' ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {bet.result.charAt(0).toUpperCase() + bet.result.slice(1)}
                          </span>
                        )}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
                        bet.pl > 0 ? 'text-green-600' :
                        bet.pl < 0 ? 'text-red-600' :
                        'text-gray-900'
                      }`}>
                        £{bet.pl.toFixed(2)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
                        calculateCumulativePL(performanceData, index) > 0 ? 'text-green-600' :
                        calculateCumulativePL(performanceData, index) < 0 ? 'text-red-600' :
                        'text-gray-900'
                      }`}>
                        £{calculateCumulativePL(performanceData, index).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <button
                          onClick={() => {
                            setBetToDelete({
                              id: bet.id,
                              date: bet.date,
                              runner: bet.runner
                            });
                            setShowDeleteConfirmation(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && betToDelete && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-4 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setShowDeleteConfirmation(false)}
            />

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete Bet
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the bet for {betToDelete.runner}? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}